import type { HttpErrorResponse } from '@angular/common/http';

import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';
import type {
  OpenQuestionsTemplate,
  OpenQuestionsTemplatePayload,
  SaveQuestionPayload,
} from '@recruitee/open-questions-templates-types';

const { actions, selectors, reducer, namespace } =
  createEntityCollectionStore<OpenQuestionsTemplate>()('OpenQuestionsTemplates', {
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const reducerFunctions = getEntityCollectionActions(
        adapter,
        ({ id }: { id?: number }) => id!,
      );

      return {
        fetchCollectionSuccess: (state, payload: { data: OpenQuestionsTemplate[] }) =>
          reducerFunctions.fetchCollectionSuccess(state, payload),
        fetchCollectionFailed: (state, payload: { error: HttpErrorResponse; scope: string }) =>
          reducerFunctions.fetchCollectionFailed(state, payload),

        fetchItem: (state, payload: { id: number }) => adapter.markAsPending(state),
        fetchItemSuccess: (state, payload: { data: OpenQuestionsTemplate }) =>
          reducerFunctions.fetchOneSuccess(state, payload),

        duplicate: (state, payload: { id: number }) => adapter.markAsPending(state),
        duplicateSuccess: (state, payload: OpenQuestionsTemplate) =>
          pipeState(state, next => adapter.markAsLoaded(next)),
        duplicateFailed: (state, payload: HttpErrorResponse) => adapter.setError(state, payload),

        create: (state, payload: OpenQuestionsTemplatePayload) => adapter.markAsPending(state),
        createSuccess: (state, payload: OpenQuestionsTemplate) =>
          pipeState(adapter.addOne(state, payload), next => adapter.markAsLoaded(next)),
        createFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload, { entities: [], scope: null }),

        update: (state, payload: OpenQuestionsTemplatePayload) => adapter.markAsPending(state),
        updateSuccess: (state, payload: OpenQuestionsTemplate) =>
          pipeState(adapter.updateOne(state, payload), next => adapter.markAsLoaded(next)),
        updateFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload, { entities: [], scope: null }),

        delete: (state, payload: { id: number }) => adapter.markAsPending(state),
        deleteSuccess: (state, payload: number) =>
          pipeState(adapter.removeOne(state, payload), next => adapter.markAsLoaded(next)),
        deleteFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload, { entities: [], scope: null }),

        setDefault: (state, payload: { id: number; isDefault: boolean }) =>
          adapter.markAsPending(state),
        setDefaultSuccess: (state, payload: OpenQuestionsTemplate) =>
          pipeState(adapter.updateOne(state, payload), next => adapter.markAsLoaded(next)),
        setDefaultFailed: (state, payload: HttpErrorResponse) =>
          adapter.setError(state, payload, { entities: [], scope: null }),

        updatePositions: (state, payload: number[]) => adapter.markAsPending(state),
        updatePositionsSuccess: (state, payload: { data: OpenQuestionsTemplate[] }) =>
          reducerFunctions.fetchCollectionSuccess(state, payload),
        updatePositionsFailed: reducerFunctions.fetchCollectionFailed,

        saveQuestion: ngrxEvent<{ data: SaveQuestionPayload }>(),
        saveQuestionSuccess: ngrxEvent<void>(),
        saveQuestionFail: ngrxEvent<{ error: string[] }>(),

        deleteQuestion: ngrxEvent<{ templateId: string; questionId: string }>(),
        deleteQuestionSuccess: ngrxEvent<void>(),
        deleteQuestionFail: ngrxEvent<{ error: string[] }>(),

        updateQuestionsPositions: ngrxEvent<{ templateId: number; positions: number[] }>(),
        updateQuestionsPositionsSuccess: ngrxEvent<void>(),
        updateQuestionsPositionsFail: ngrxEvent<{ error: string[] }>(),
      };
    },
  });

export {
  actions as OpenQuestionsTemplatesActions,
  selectors as OpenQuestionsTemplatesSelectors,
  reducer as OpenQuestionsTemplatesReducer,
  namespace as OpenQuestionsTemplatesNamespace,
};
